package ai.jessie.web.members

import ai.jessie.shared.members.ChangeMemberRoleRequest
import ai.jessie.shared.members.InvitationChangeRequest
import ai.jessie.shared.members.IssueInvitationRequest
import ai.jessie.shared.members.MemberChangeResponse
import ai.jessie.shared.members.MemberManagementResponse
import ai.jessie.shared.members.RemoveMemberRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-005. Every call names the organisation; the server alone decides owner authority.
interface MemberManagementPort {
    suspend fun members(organisationId: String): MemberManagementResponse

    suspend fun issue(organisationId: String, request: IssueInvitationRequest): MemberChangeResponse

    suspend fun resend(
        organisationId: String,
        invitationId: String,
        request: InvitationChangeRequest,
    ): MemberChangeResponse

    suspend fun revoke(
        organisationId: String,
        invitationId: String,
        request: InvitationChangeRequest,
    ): MemberChangeResponse

    suspend fun changeRole(
        organisationId: String,
        memberId: String,
        request: ChangeMemberRoleRequest,
    ): MemberChangeResponse

    suspend fun remove(
        organisationId: String,
        memberId: String,
        request: RemoveMemberRequest,
    ): MemberChangeResponse
}

val LocalMemberManagementPort =
    staticCompositionLocalOf<MemberManagementPort> {
        error("Member management port must be supplied by the application")
    }

class BrowserMemberManagementPort : MemberManagementPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun members(organisationId: String) =
        json.decodeFromString<MemberManagementResponse>(
            apiRequest(json, "${scope(organisationId)}/members")
        )

    override suspend fun issue(organisationId: String, request: IssueInvitationRequest) =
        change("${scope(organisationId)}/invitations", json.encodeToString(request))

    override suspend fun resend(
        organisationId: String,
        invitationId: String,
        request: InvitationChangeRequest,
    ) =
        change(
            "${scope(organisationId)}/invitations/${target(invitationId)}/resend",
            json.encodeToString(request),
        )

    override suspend fun revoke(
        organisationId: String,
        invitationId: String,
        request: InvitationChangeRequest,
    ) =
        change(
            "${scope(organisationId)}/invitations/${target(invitationId)}/revoke",
            json.encodeToString(request),
        )

    override suspend fun changeRole(
        organisationId: String,
        memberId: String,
        request: ChangeMemberRoleRequest,
    ) =
        change(
            "${scope(organisationId)}/members/${target(memberId)}/role",
            json.encodeToString(request),
        )

    override suspend fun remove(
        organisationId: String,
        memberId: String,
        request: RemoveMemberRequest,
    ) =
        change(
            "${scope(organisationId)}/members/${target(memberId)}/remove",
            json.encodeToString(request),
        )

    private suspend fun change(path: String, body: String) =
        json.decodeFromString<MemberChangeResponse>(apiRequest(json, path, body))

    private fun scope(organisationId: String): String {
        require(validOrganisationId(organisationId))
        return "/organisations/$organisationId"
    }

    // Listed public IDs are UUIDs; anything else never becomes part of a request path.
    private fun target(publicId: String): String {
        require(validOrganisationId(publicId))
        return publicId
    }
}
